import { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ExclamationIcon } from '@/components/icons/exclamation';
import { Input } from '@/components/ui/input';
import { TiptapEditor } from '@/components/ui/tiptap-editor';
import { ActionButtonAttributes } from '@/components/ui/tiptap-editor/extensions/action-button';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useAccountSlug } from '@/hooks/use-account-slug';
import { useBaseWorkerAttributes } from '@/hooks/use-base-worker-attributes';
import { domainMeta } from '@/utils/domain-meta';
import { normalizeHtmlContent } from '@/utils/string-util';
interface MessageContentProps {
  openingId: string;
}

export function MessageContent({ openingId }: MessageContentProps) {
  const intl = useIntl();
  const accountSlug = useAccountSlug();
  const { hookForm } = useJobPostingContext();
  const {
    register,
    control,
    watch,
    formState: { errors },
    getValues,
    setValue,
  } = hookForm;

  const openingSlug = getValues(`jobDetails.${openingId}.slug`);
  const actionButtonLink = `${domainMeta.monolithOrigin}/${accountSlug}/apply/${openingSlug}`;
  const campaignType = watch(`channels.pool.poolDetails.campaignType`);
  const isEmailCampaign = campaignType === 'email';

  const subjectError =
    errors?.channels?.pool?.poolDetails?.openingMessages?.[openingId]?.subject
      ?.message;
  const bodyError =
    errors?.channels?.pool?.poolDetails?.openingMessages?.[openingId]?.body
      ?.message;

  // Get base worker attributes (hardcoded defaults)
  // TODO: Add API integration later to fetch custom attributes
  const baseAttributes = useBaseWorkerAttributes();

  const handleActionButtonsChange = useCallback(
    (buttons: ActionButtonAttributes[]) => {
      if (buttons.length <= 0) return;

      setValue(
        `channels.pool.poolDetails.openingMessages.${openingId}.buttons`,
        buttons.map(b => ({
          id: b.buttonId,
          label: b.value,
          value: b.link,
        })),
        { shouldDirty: true },
      );
    },
    [openingId, setValue],
  );

  return (
    <div className="relative flex flex-1 flex-col gap-3 overflow-y-auto px-6 py-5">
      {/* Description */}
      <Typography variant="body1" className="!text-fx-neutral-400">
        <FormattedMessage
          defaultMessage="Sends when campaign is activated"
          description="message content description"
        />
      </Typography>

      <div className="bg-fx-neutral-200 h-px w-full" />

      {/* Subject */}
      {isEmailCampaign && (
        <>
          <div className="-mt-2 flex items-center gap-4">
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Subject"
                description="Subject of the message content"
              />
            </Typography>
            <Input
              {...register(
                `channels.pool.poolDetails.openingMessages.${openingId}.subject`,
              )}
              placeholder={intl.formatMessage({
                defaultMessage: 'Type your subject',
                description: 'Placeholder for the subject input',
              })}
              className="w-full"
              isBorderless
            />
            {subjectError && <RequiredError />}
          </div>
          <div className="bg-fx-neutral-200 -mt-2 h-px w-full" />
        </>
      )}

      <Controller
        name={`channels.pool.poolDetails.openingMessages.${openingId}.body`}
        control={control}
        render={({ field }) => (
          <div className="flex min-h-0 flex-1 flex-col">
            <TiptapEditor
              appearance="borderless"
              height="full"
              preset={isEmailCampaign ? 'full' : undefined}
              features={isEmailCampaign ? undefined : []}
              content={field.value ?? ''}
              onChange={(html: string) => {
                // Normalize empty HTML content to null to properly clear form state
                const normalizedValue = normalizeHtmlContent(html);
                field.onChange(normalizedValue);
              }}
              onActionButtonsChange={handleActionButtonsChange}
              placeholder={intl.formatMessage({
                defaultMessage: 'Type your message...',
                description: 'Placeholder for the job description editor',
              })}
              workerAttributes={baseAttributes}
              actionButtonDefaults={{
                label: intl.formatMessage({
                  defaultMessage: 'Apply to job',
                  description:
                    'Default CTA text for new action buttons in pool message editor',
                }),
                link: actionButtonLink,
                backgroundColor: '#2600FF',
              }}
            />
          </div>
        )}
      />

      {bodyError && (
        <div className="absolute right-6 bottom-0">
          <RequiredError />
        </div>
      )}
    </div>
  );
}

function RequiredError() {
  return (
    <div className="flex items-center gap-1">
      <ExclamationIcon />
      <Typography variant="small1" className="!text-fx-rose">
        <FormattedMessage
          defaultMessage="Required"
          description="Input is required"
        />
      </Typography>
    </div>
  );
}
